/**
 * The onboarding step that allows to invite a company members
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import isEmail from 'validator/lib/isEmail';
import { addCompanyUsers } from 'redux/modules/companies';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { USERS } from 'routes';
import { flatten, fill, debounce } from 'lodash';
import TeamMembersAvatars from './assets/TeamMembersAvatars.svg';
import { typingDebounceTimeout } from 'theme/uiConfig';
import i18n from 'i18n/i18n';

const localize = i18n.loc.localize;

const styles = {
	root: {
		flexGrow: '1',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center'
	},
	avatars: {
		width: 332,
		height: 52,
		margin: '40px 0 25px',
		background: `no-repeat url(${TeamMembersAvatars}) center`
	},
	title: {
		fontWeight: 'bold',
		marginBottom: 63
	},
	fields: {
		display: 'flex',
		flexDirection: 'column',
		marginBottom: 20
	},
	field: {
		margin: '0 auto 10px'
	},
	navigation: {
		marginTop: 'auto',
		display: 'flex',
		flexDirection: 'column',
		paddingTop: 20
	}
};

const ValidationSchema = {
	email: (value) => {
		if (value && !isEmail(value)) {
			return localize('onboarding-members-email-invalid');
		}
	},
	duplicate: (duplicatedIndices, currentIndex) => {
		if (duplicatedIndices.indexOf(currentIndex) !== -1) {
			return localize('onboarding-members-email-duplicate');
		}
	}
};

const DEFAULT_NUMBER_OF_FIELDS = 3;
const MAX_NUMBER_OF_FIELDS = 8;

@withStyles(styles)
class TeamMembersStep extends Component {

	constructor() {
		super();
		this.onFieldAdd = this.onFieldAdd.bind(this);
		this.onSave = this.onSave.bind(this);
		this.onStepSkipped = this.onStepSkipped.bind(this);
		this.validateFields = debounce(this.validateFields.bind(this), typingDebounceTimeout);
		this.state = {
			saving: false,
			valid: false,
			values: fill(Array(DEFAULT_NUMBER_OF_FIELDS), ''),
			errors: fill(Array(DEFAULT_NUMBER_OF_FIELDS), '')
		};
	}

	render() {
		const { classes } = this.props;
		return (
			<div className={classes.root}>
				<div className={classes.avatars} />
				<Typography
					variant='h3'
					align='center'
					className={classes.title}
					gutterBottom
				>
					{localize('onboarding-members-title')}
				</Typography>

				<div className={classes.fields}>
					{this.renderFields()}
				</div>

				<Button
					variant='contained'
					color='primary'
					data-tid='button-add-more-members'
					onClick={this.onFieldAdd}
					disabled={this.state.values.length === MAX_NUMBER_OF_FIELDS}
				>
					{localize('onboarding-members-add-more')}
				</Button>

				<div className={classes.navigation}>
					<Button
						variant='contained'
						size='large'
						data-tid='button-team-members-step-next'
						color='primary'
						onClick={this.onSave}
						disabled={!this.state.valid || this.state.saving}
					>
						{this.state.saving ?
							<CircularProgress size={26} color='inherit' /> :
							localize('onboarding-members-next')}
					</Button>
					<Button
						disableRipple
						data-tid='button-add-more-members-skip'
						onClick={this.onStepSkipped}
						size='small'>{localize('onboarding-members-skip')}</Button>
				</div>
			</div>
		);
	}

	renderFields() {
		const { classes } = this.props;
		const { values, errors } = this.state;

		return values.map((value, index) => {
			return (
				<TextField
					key={`email-${index}`}
					type='email'
					placeholder={localize('onboarding-members-email-placeholder')}
					className={classes.field}
					value={value}
					onChange={this.onFieldChange(index)}
					helperText={errors[index]}
					error={!!errors[index]}
					InputProps={{
						disableUnderline: true
					}}
					inputProps={{
						'data-tid': `input-invite-email-address-${index}`
					}}
					autoComplete='new-password'
				/>
			);
		});
	}

	onFieldChange(index) {
		return (event) => {
			const { value } = event.target;
			const values = [ ...this.state.values ];

			values[index] = value;

			this.setState({ values, valid: false }, () => this.validateFields());
		};
	}

	onFieldAdd() {
		if (this.state.values.length <= MAX_NUMBER_OF_FIELDS) {
			this.setState({
				values: [ ...this.state.values, '' ],
				errors: [ ...this.state.errors, '' ]
			});
		}
	}

	onSave() {
		if (this.state.valid) {
			const members = [];

			for (const value of this.state.values) {
				if (value && value.length > 2) {
					members.push({
						email: value,
						role: 'basic',
					});
				}
			}

			if (members.length) {
				this.setState({ saving: true });
				this.props.addCompanyUsers(...members).then(() => {
					this.props.onboarding.fulfillStep();
				});
			}
		}
	}

	onStepSkipped() {
		this.props.onboarding.skipStep();
	}

	validateFields() {
		const { values } = this.state;

		/*
			Map is an object where keys are field values and values are arrays of the indexes:
			{
				'email@example.net': [0, 2],
				'test@xara.com': [1]
			}
			This means there are 3 fields, where the 1st and 3rd fields have same value:
				email@example.net
				test@xara.com
				email@example.net
		*/
		const map = values.reduce((accumulator, value, index) => {
			value && (accumulator[value] = [...(accumulator[value] || []), index]);
			return accumulator;
		}, {});

		// Filters map object by checking duplicates and creates a flat array of duplicated indices
		const duplicatedIndices = flatten(Object.values(map).filter(value => value.length > 1));

		const errors = values.map((value, index) => {
			const formatError = ValidationSchema.email(value);
			const duplicateError = ValidationSchema.duplicate(duplicatedIndices, index);

			// Set errors for non-empty field
			if (value.length) {
				if (formatError) {
					return formatError;
				} else if (duplicateError) {
					return duplicateError;
				}
			}

			return '';
		});

		this.setState({ errors, valid: this.areValid(errors) });
	}

	areValid(errors) {
		const { values } = this.state;
		return errors.filter(error => error).length === 0 && values.filter(value => value).length > 0;
	}

}

TeamMembersStep.propTypes = {
	classes: PropTypes.object,
	onboarding: PropTypes.object,
	addCompanyUsers: PropTypes.func
};

TeamMembersStep.oId = 'team-members';
TeamMembersStep.oTitle = localize('onboarding-members-o-title');
TeamMembersStep.oMessage = localize('onboarding-members-o-message');
TeamMembersStep.oRoute = USERS;

const mapDispatchToProps = dispatch => bindActionCreators({
	addCompanyUsers
}, dispatch);

export default connect(
	undefined,
	mapDispatchToProps
)(TeamMembersStep);



// WEBPACK FOOTER //
// ./src/containers/OnBoarding/Steps/TeamMembersStep.js